<template lang="pug">
	.audio-tracker-tools.flex-row.space.align-center(@dblclick.stop="", @click.stop="")
		template(v-if="track && currentTool.tool")
			span(v-if="!currentTool.tool.hideName") {{currentTool.tool.name()}}:
			Loader(v-if="isToolProcessing", :text="$tCaption('Processing') + '..'")
			template(v-else)
				.trim.flex-row.space(v-if="currentTool.tool.selectionPoint")
					.button.flex-row.center.align-center.success(@click.stop="applyTool", v-if="currentTool.tool.selection && selection.duration || selection.start", @mousedown.stop="")
						i(v-if="currentTool.tool.icon", :class="[currentTool.tool.icon]")
						span {{$tCaption('Confirm', '@actions')}}
					.button.flex-row.center.align-center(@click.stop="cancelTool", @mousedown.stop="")
						i.fas.fa-times
						span {{$tCaption('Cancel', '@actions')}}
				.trim.flex-row.space(v-if="['trim-silence', 'normalize'].indexOf(currentTool.tool.id) > -1")
					DxNumberBox(format="#0.## dB", :value.sync="currentTool.dbLevel", :width="80", :show-spin-buttons="true", :step="currentTool.dbStep", @mousedown.stop="")
					.button.flex-row.center.align-center.success(@click.stop="applyTool", @mousedown.stop="")
						i.fas.fa-check
						span {{$tCaption('Confirm', '@actions')}}
					.button.flex-row.center.align-center(@click.stop="cancelTool", @mousedown.stop="")
						i.fas.fa-times
						span {{$tCaption('Cancel', '@actions')}}
				.trim.flex-row.space(v-if="currentTool.tool.id === 'fadeInOut'", @mousemove.stop="", @mousedown.stop="")
					.flex-col.space.fade-in-out
						.flex-row.space.align-center
							.fade-in-out-caption.font-small.upper.grey {{$tCaption('Fade in (sec)')}}
							DxNumberBox(:value="fadeIn.value", @value-changed="e => onFadeChange(0, e.value)", format="#0.##", :width="40")
							DxSelectBox(:dataSource="fadeInOutTypes", display-expr="text", value-expr="id", :value.sync="fadeIn.type", :width="80", :dropDownOptions="{ minWidth: '160px' }")
						.flex-row.space.align-center
							.fade-in-out-caption.font-small.upper.grey {{$tCaption('Fade out (sec)')}}
							DxNumberBox(:value="fadeOut.value", @value-changed="e => onFadeChange(1, e.value)", format="#0.##", :width="40")
							DxSelectBox(:dataSource="fadeInOutTypes", display-expr="text", value-expr="id", :value.sync="fadeOut.type", :width="80", :dropDownOptions="{ minWidth: '160px' }")
						.flex-row.space.justify-end
							.button.flex-row.center.align-center.success(@click.stop="applyTool", @mousedown.stop="")
								i.fas.fa-check
								span {{$tCaption('Confirm', '@actions')}}
							.button.flex-row.center.align-center(@click.stop="cancelTool", @mousedown.stop="")
								i.fas.fa-times
								span {{$tCaption('Cancel', '@actions')}}
				.trim.flex-row.space(v-if="['volumeEnvelope', 'pitchEnvelope'].includes(currentTool.tool.id)")
					.button.flex-row.center.align-center.success(@click.stop="applyTool")
						i(v-if="currentTool.tool.icon", :class="[currentTool.tool.icon]")
						span {{$tCaption('Confirm', '@actions')}}
					.button.flex-row.center.align-center(v-if="currentTool.tool.pencilTool", @click.stop="toggleEnvelopePencilTool", :title="$tCaption('Pencil tool')", :class="{ success: activeSubTool === 'pencilTool' }")
						fas(icon="pencil")
					.button.flex-row.center.align-center(v-if="currentTool.tool.selectionTool", @click.stop="toggleEnvelopeSelectionTool", :title="$tCaption('Selection tool')", :class="{ success: activeSubTool === 'selectionTool' }")
						fas(icon="expand")
					.button.flex-row.center.align-center(@click.stop="clearCurrentEnvelope")
						i.fas.fa-redo
						span {{$tCaption('Clear', '@actions')}}
					.button.flex-row.center.align-center(@click.stop="cancelTool", @mousedown.stop="")
						i.fas.fa-times
						span {{$tCaption('Cancel', '@actions')}}
				.trim.flex-col.space.clip-settings(v-if="['clip-settings'].indexOf(currentTool.tool.id) > -1")
					ClipSettings(:playbackRate.sync="playbackRate", :pitchRate.sync="pitchRate", :volumeRate.sync="volumeRate", :clipMuted.sync="clipMuted")
					.flex-row.justify
						.flex-row.align-center.space
							.button.flex-row.center.align-center(@click.stop="resetTool", @mousedown.stop="")
								fas(icon="refresh")
								span {{$tCaption('Reset', '@actions')}}
						.flex-row.align-center.space
							.button.flex-row.center.align-center.success(@click.stop="applyTool", @mousedown.stop="")
								fas(icon="check")
								span {{$tCaption('Confirm', '@actions')}}
							.button.flex-row.center.align-center(@click.stop="cancelTool", @mousedown.stop="")
								fas(icon="times")
								span {{$tCaption('Cancel', '@actions')}}
</template>
<script>
	import _ from 'lodash';
	import storage from 'storage';
	import Popup from 'app-core/Popup.vue';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxNumberBox from 'devextreme-vue/number-box';
	import DxSlider from 'devextreme-vue/slider';
	import Loader from 'app-core/Loader.vue';
	import ClipSettings from './AudioTrackerToolsClipSettings.vue';

	export default {
		translationCategory: 'audioTracker.AudioTrackerTools',
		name: 'audio-tracker-tools',
		components: { Popup, DxNumberBox, Loader, DxSelectBox, DxSlider, ClipSettings },
		props: ['track', 'tracker'],
		data() {
			return {
				tools: [
					{ id: 'cut', name: () => this.$tCaption('Cut'), icon: 'fas fa-cut',  defaults: { start: null, end: null }, selection: true, selectionPoint: true },
					{ id: 'trim-silence', name: () => this.$tCaption('Trim'), defaults: { dbLevel: 0.1, dbStep: 0.1 } },
					{ id: 'clip-settings', name: () => this.$tCaption('Clip settings'), defaults: { }, hideName: true },
					{ id: 'normalize', name: () => this.$tCaption('Normalize'), defaults: { dbLevel: -3, dbStep: 1 } },
					{ id: 'split', name: () => this.$tCaption('Split'), icon: 'fas fa-grip-lines-vertical', defaults: { start: null, end: false }, selectionPoint: true },
					{ id: 'fadeInOut', name: () => this.$tCaption('Fade In/Out'), icon: 'fas fa-think-peaks', defaults: { }, selectionPoint: false, hideName: true },
					{ id: 'volumeEnvelope', name: () => this.$tCaption('Volume envelope'), icon: 'fas fa-vector-square', defaults: { }, selectionPoint: false, hideName: false, pencilTool: true, selectionTool: true },
					{ id: 'pitchEnvelope', name: () => this.$tCaption('Pitch envelope'), icon: 'fas fa-arrows-up-down', defaults: { }, selectionPoint: false, hideName: false, selectionTool: true },
				],
				currentTool: {
					tool: null,
					dbLevel: null,
					dbStep: null,
					start: null,
					end: null,
					isSelecting: false
				},
				isToolProcessing: false,
				selectionVisible: false,
				selectionPointVisible: false,
				fadeInOutTypes: [
					{ id: 'linear', text: this.$tCaption('Linear') },
					{ id: 'exponential', text: this.$tCaption('Logarithmic') }
				],
				pitchRate: 1,
				playbackRate: 1,
				volumeRate: 1,
				clipMuted: false,
				activeSubTool: null,
				initialJsonConfig: _.cloneDeep(this.track.insertion.JsonConfig)
			};
		},
		computed: {
			selection() {
				var items = _.sortBy([this.currentTool.start, this.currentTool.end], v => v);
				return {
					start: items[0],
					duration: (items[1] - items[0]) || 0,
					end: items[1]
				};
			},
			fadeIn() {
				return this.track.insertion.JsonConfig.fadeIn;
			},
			fadeOut() {
				return this.track.insertion.JsonConfig.fadeOut;
			}
		},
		watch: {
			'currentTool.tool'(v) {
				if (__DEBUG__)
					console.debug('Tool changed', { v });
				this.$emit('tool-changed', v?.id);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.tools = this;

			this.$eventsBus.$on('tracker-tool-fade-change', this.onTrackerToolFadeChange);
		},
		destroyed() {
			this.$emit('tool-changed', null);

			this.$eventsBus.$off('tracker-tool-fade-change', this.onTrackerToolFadeChange);
		},
		methods: {	
			onFadeChange(kind, value) {
				this.$eventsBus.$emit('tracker-tool-fade-change', [kind === 0 ? value : this.fadeIn.value, kind === 1 ? value : this.fadeOut.value], 'tools');
			},
			onTrackerToolFadeChange(fades, info) {
				if (__DEBUG__)
					console.debug('onTrackerToolFadeChange', fades, info);

				this.fadeIn.value = fades[0] || 0;
				this.fadeOut.value = fades[1] || 0;

				this.track.insertion.JsonConfig.fadeIn.value = fades[0];
				this.track.insertion.JsonConfig.fadeOut.value = fades[1];
			},
			toggleEnvelopePencilTool() {
				this.activeSubTool = this.activeSubTool === 'pencilTool' ? null : 'pencilTool';
				this.$eventsBus.$emit('track-envelope-pencil-tool', this.activeSubTool === 'pencilTool');
			},
			toggleEnvelopeSelectionTool() {
				this.activeSubTool = this.activeSubTool === 'selectionTool' ? null : 'selectionTool';
				this.$eventsBus.$emit('track-envelope-selection-tool', this.activeSubTool === 'selectionTool');
			},
			clearCurrentEnvelope() {
				this.$eventsBus.$emit('track-envelope-clear');
			},
			processTrackAction(time, action) {
				if (!this.currentTool.tool || !this.currentTool.tool.selectionPoint) return false;
				if (__DEBUG__ && (action === 'mouse-down' || action === 'mouse-up'))
					console.debug('Process track action', { time, action });
				switch(action) {
					case 'mouse-down':
						this.currentTool.isSelecting = true;
						this.currentTool.start = time;
						break;
					case 'mouse-move':
						if (this.currentTool.isSelecting)
							this.currentTool.end = time;
						break;
					case 'mouse-up':
						if (this.currentTool.isSelecting) {
							this.currentTool.isSelecting = false;
							this.currentTool.end = time;
						}
						break;
				}
				return true;
			},
			cancelTool() {
				if (__DEBUG__)
					console.debug('Cancel tool');
				if (['volumeEnvelope', 'pitchEnvelope'].includes(this.currentTool?.tool?.id)) {
					this.$eventsBus.$emit('track-envelope-cancel');
				}
				if (['fadeInOut'].includes(this.currentTool?.tool?.id) && this.initialJsonConfig) {
					_.assign(this.track.insertion.JsonConfig, this.initialJsonConfig);
				}
				this.currentTool.tool = null;
				this.isToolProcessing = false;
				this.selectionVisible = false;
				this.selectionPointVisible = false;
				this.$emit('tool-changed', null);
				this.$emit('finished');
			},
			resetTool() {
				switch(this.currentTool.tool.id) {
					case 'clip-settings':
						this.playbackRate = 1;
						this.pitchRate = 1;
						this.volumeRate = 1;
						this.clipMuted = false;
						break;
				}
			},
			setTool(toolId, start) {
				if (__DEBUG__)
					console.debug('Set tool', { toolId, start });
				this.isToolProcessing = false;
				var tool = this.tools.filter(f => f.id === toolId)[0];
				this.currentTool.tool = tool;
				this.currentTool.start = start;
				this.currentTool.end = null;
				this.currentTool.dbLevel = storage.prop(`audio-tracker-${toolId}-db-level`).value || tool.defaults.dbLevel;
				this.currentTool.dbStep = tool.defaults.dbStep;
				this.selectionVisible = !!tool.selection;
				this.selectionPointVisible = !!tool.selectionPoint;

				if (toolId === 'fadeInOut') {
					this.fadeIn = _.assign({
						value: 0,
						type: 'exponential'
					}, this.track?.insertion?.JsonConfig?.fadeIn?.value && this.track?.insertion?.JsonConfig?.fadeIn || {});
					this.fadeOut = _.assign({
						value: 0,
						type: 'exponential'
					}, this.track?.insertion?.JsonConfig?.fadeOut?.value && this.track?.insertion?.JsonConfig?.fadeOut || {});
				}

				if (toolId === 'clip-settings') {
					this.pitchRate = this.track.pitchRate;
					this.playbackRate = this.track.playbackRate;
					this.volumeRate = this.track.volumeRate;
					this.clipMuted = this.track.clipMuted;
				}
			},
			applyTool() {
				if (!this.currentTool.tool) return;
				var tool = this.currentTool.tool;
				if (tool.defaults.dbLevel)
					storage.prop(`audio-tracker-${tool.id}-db-level`).value = this.currentTool.dbLevel;
				this.isToolProcessing = true;
				var base64 = this.tracker.tracksBase64[this.track.id];

				if (__DEBUG__)
					console.debug('Apply tool', { base64 });

				var data = base64.split(';base64,')[1];
				var ext = this.$Data.getBase64AudioExtension(base64);
				var method = null;
				var additionalParams = {};
				var isCuttingBeginning = false;
				switch(this.currentTool.tool.id) {
					case 'split':
						var splitPosition = Math.max(0, this.selection.start - this.track.start);
						var trimmedDuration = this.track.getTrimmedDuration();
						if (splitPosition > 0 && splitPosition < trimmedDuration) {
							if (__DEBUG__)
								console.debug('Split tool', { splitPosition, trimmedDuration, track: this.track, newOffset: trimmedDuration - splitPosition });
							const endOffset = this.track.endOffset;
							this.track.endOffset += trimmedDuration - splitPosition;
							this.$emit('duplicate-track', this.track, {
								StartTime: parseInt((this.track.start + splitPosition) * 1000),
								StartOffset: parseInt((this.track.startOffset + splitPosition) * 1000),
								EndOffset: parseInt(endOffset * 1000),
							});
						}
						break;
					case 'cut':
						var startOffset = Math.max(0, this.selection.start - this.track.start);
						var duration = Math.max(0, this.selection.end - Math.max(this.selection.start, this.track.start));
						var endOffset = Math.max(0, this.track.start + this.track.getTrimmedDuration() - this.selection.end);
						if (duration > 0) {
							this.track.startOffset += startOffset;
							this.track.start = Math.max(this.selection.start, this.track.start);
							this.track.endOffset += endOffset;
							if (__DEBUG__)
								console.debug('Cut', { startOffset, duration, endOffset, ts: this.track.start, trimDur:  this.track.getTrimmedDuration(), selEnd: this.selection.end, to: [this.track.startOffset, this.track.endOffset] });
						}
						break;
					case 'trim-silence':
						method = 'TrimAudio';
						this.trimmedSilence = true;
						additionalParams = {
							DbLevel: this.currentTool.dbLevel
						};
						break;
					case 'normalize':
						method = 'NormalizeAudio';
						this.normalized = true;
						additionalParams = {
							DbLevel: this.currentTool.dbLevel
						};
						break;
					case 'fadeInOut':
						this.track.insertion.JsonConfig.fadeIn = _.clone(this.fadeIn);
						this.track.insertion.JsonConfig.fadeOut = _.clone(this.fadeOut);
						break;
					case 'volumeEnvelope':
					case 'pitchEnvelope':
						this.$eventsBus.$emit('track-envelope-save');
						break;
					case 'clip-settings':
						this.track.playbackRate = this.playbackRate;
						this.track.pitchRate = this.pitchRate;
						this.track.volumeRate = this.volumeRate;
						this.track.clipMuted = this.clipMuted;
						break;
				}

				if (method) {
					var track = this.track;
					var currentTool = this.currentTool.tool;
					this.isLoading = true;
					this.$Data.postExternalApi(`audio/${method}`, {
						MediaExtension: ext,
						...additionalParams,
						Base64Content: data
					}, {
						xhr: () => {
							var xhr = new XMLHttpRequest();
							xhr.responseType= 'blob';
							return xhr;
						},
						processData: false,
						dataType: 'binary'
					})
					.then(res => {
						if (this._isDestroyed) return;
						if (__DEBUG__)
							console.info('Get tool audio data response', { res });

						if (!res.size) {
							this.$notifyError(this.$tCaption('An error occured while processing audio.'));
							return null;
						}

						var fr = new FileReader;
						fr.onloadend = () => {
							var r = fr.result;
							if (this._isDestroyed) return;
							if (__DEBUG__)
								console.log(`${method} operation finished`, { r, base64, isCuttingBeginning, additionalParams });
							if (r.ErrorMessage) {
								this.$notifyError(r.ErrorMessage);
								this.$emit('finished');
							} else {
								this.tracker.setBase64ForTrack(track, r, true);
								this.$emit('finished', true, track);
								switch(currentTool?.id) {
									case 'cut':
										if (isCuttingBeginning) {
											track.start += duration;
											track.duration -= duration;
										}
										break;
								}
								this.currentTool.tool = null;
							}
						};
						fr.readAsDataURL(res);
						return res;
					})
					.catch(() => this.$emit('finished'))
					.finally(() => {
						if (this._isDestroyed) return;
						this.isToolProcessing = false;
						this.isLoading = false;
						this.currentTool.tool = null;
						this.$emit('tool-changed', null);
					});
				} else {
					setImmediate(() => {
						if (this._isDestroyed) return;
						this.$emit('finished');
						this.isToolProcessing = false;
						this.currentTool.tool = null;
						this.$emit('tool-changed', null);
					});
				}

				this.selectionVisible = false;
			},
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-tools {
		.fade-in-out {
			.dx-texteditor-input {
				font-size: 12px !important;
			}
		}

		.fade-in-out-caption {
			min-width: 90px;
			text-align: right;
		}

		.clip-settings {
			width: 314px;
		}
	}
</style>
